import { useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useBuildActionContext } from "@/contexts/DetailActionContext";
import type {
  BaseEntity,
  SimpleTableConfig,
  EntityHooks,
} from "../../UniversalCRMTable/types/shared";

export function useDetailModalActions<T extends BaseEntity>({
  item,
  hooks,
  refetch,
  entityName,
  config,
  appUser: _unusedAppUser,
  openEditModal,
}: {
  item: T | undefined;
  hooks: Pick<EntityHooks<T>, "useSingle" | "useUpdate" | "useDelete">;
  refetch: () => void;
  entityName: string;
  config: SimpleTableConfig<T>;
  appUser: { id?: string } | null | undefined;
  openEditModal: () => void;
}) {
  void _unusedAppUser;
  // Mutations - universal hooks
  const updateMutation = hooks.useUpdate();
  const deleteMutation = hooks.useDelete();

  // Build action context using centralized hook
  // Create a minimal EntityHooks-compatible object with the required methods
  // useBuildActionContext expects full EntityHooks but only uses useUpdate, useDelete, useSingle
  const hooksForContext = useMemo(
    () =>
      ({
        ...hooks,
        usePaginated: () => ({ data: undefined, isLoading: false, error: null, refetch: () => {} }),
        useInfinite: () => ({
          data: undefined,
          isLoading: false,
          error: null,
          refetch: () => {},
          hasNextPage: false,
          isFetchingNextPage: false,
          fetchNextPage: () => {},
        }),
        useCreate: () => ({ mutateAsync: async () => {}, isPending: false }),
      }) as unknown as EntityHooks<T>,
    [hooks]
  );

  const actionContext = useBuildActionContext({
    item,
    hooks: hooksForContext,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
    onDeleteComplete: () => {},
  });

  // Handle inline edit
  const handleInlineEdit = useCallback(
    async (updatedItem: T) => {
      try {
        // Build minimal update payload; GraphQL hooks run prepareEntityGraphQlMutationInput
        const inputPayload: Partial<T> = {};
        Object.keys(updatedItem || {}).forEach((key) => {
          const k = key as keyof T;
          if ((item as any)?.[k] !== (updatedItem as any)[k]) {
            inputPayload[k] = updatedItem[k];
          }
        });
        const payload: Partial<T> & { id: string } = {
          id: String((updatedItem as any).id),
          ...inputPayload,
        };

        // If nothing actually changed, avoid sending an empty update to GraphQL/Supabase
        if (Object.keys(inputPayload).length === 0) {
          toast.info("No changes to save");
          return;
        }

        await updateMutation.mutateAsync(payload);

        toast.success(`${config.displayName} updated successfully`);
        
        // For inline edits, do NOT refetch to avoid showing loading state
        // The mutation should have already updated the cache via TanStack Query
        // If you need to refetch specific data, do it in a background task
        // refetch(); // REMOVED - causes loading state to show
      } catch (error) {
        console.error("Error updating item:", error);
        toast.error("Failed to update item");
      }
    },
    [item, updateMutation, config.displayName]
  );

  // Handle edit action
  const handleEdit = useCallback(
    (item: T) => {
      openEditModal();
    },
    [openEditModal]
  );

  // Handle delete action
  const handleDelete = useCallback(
    async (item: T) => {
      if (
        window.confirm(`Are you sure you want to delete this ${config.displayName.toLowerCase()}?`)
      ) {
        try {
          await deleteMutation.mutateAsync(item.id);

          toast.success(`${config.displayName} deleted successfully`);
          // Modal will close automatically via global stack
        } catch (error) {
          console.error("Error deleting item:", error);
          toast.error("Failed to delete item");
        }
      }
    },
    [deleteMutation, config.displayName]
  );

  return {
    actionContext,
    handleInlineEdit,
    handleEdit,
    handleDelete,
    updateMutation,
    deleteMutation,
  };
}
